import * as i0 from '@angular/core';
import { Inject, Injectable, InjectionToken, makeEnvironmentProviders, Directive, EventEmitter, HostListener, HostBinding, Output, ViewEncapsulation, Component, SecurityContext, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { DOCUMENT, CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';
import { trigger, state, transition, style, animate } from '@angular/animations';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import * as i2 from '@angular/cdk/layout';
import * as i3 from '@windmill/ng-windmill/shared';
import * as i2$1 from '@angular/platform-browser';

/**
 * A `ComponentPortal` is a portal that instantiates some Component upon attachment.
 */
class ComponentPortal {
    /** The type of the component that will be instantiated for attachment. */
    component;
    /**
     * [Optional] Where the attached component should live in Angular's *logical* component tree.
     * This is different from where the component *renders*, which is determined by the PortalHost.
     * The origin necessary when the host is outside of the Angular application context.
     */
    viewContainerRef;
    /** Injector used for the instantiation of the component. */
    injector;
    _attachedHost;
    constructor(component, injector) {
        this.component = component;
        this.injector = injector;
    }
    /** Attach this portal to a host. */
    attach(host, newestOnTop) {
        this._attachedHost = host;
        return host.attach(this, newestOnTop);
    }
    /** Detach this portal from its host */
    detach() {
        const host = this._attachedHost;
        if (host) {
            this._attachedHost = null;
            return host.detach();
        }
    }
    /** Whether this portal is attached to a host. */
    get isAttached() {
        return this._attachedHost !== null;
    }
    /**
     * Sets the PortalHost reference without performing `attach()`. This is used directly by
     * the PortalHost when it is performing an `attach()` or `detach()`.
     */
    setAttachedHost(host) {
        this._attachedHost = host;
    }
}
/**
 * Partial implementation of PortalHost that only deals with attaching a ComponentPortal
 */
class BasePortalHost {
    /** The portal currently attached to the host. */
    _attachedPortal;
    /** A function that will permanently dispose this host. */
    _disposeFn;
    attach(portal, newestOnTop) {
        this._attachedPortal = portal;
        return this.attachComponentPortal(portal, newestOnTop);
    }
    detach() {
        if (this._attachedPortal) {
            this._attachedPortal.setAttachedHost();
        }
        this._attachedPortal = null;
        if (this._disposeFn) {
            this._disposeFn();
            this._disposeFn = null;
        }
    }
    setDisposeFn(fn) {
        this._disposeFn = fn;
    }
}

class DomPortalHost extends BasePortalHost {
    _hostDomElement;
    portalHostService;
    constructor(_hostDomElement, portalHostService) {
        super();
        this._hostDomElement = _hostDomElement;
        this.portalHostService = portalHostService;
    }
    attachComponentPortal(portal, newestOnTop) {
        const componentFactory = this.portalHostService.componentFactoryResolver.resolveComponentFactory(portal.component);
        let componentRef = null;
        componentRef = componentFactory.create(portal.injector);
        this.detachComponent(componentRef);
        this.addNewToast(componentRef, newestOnTop);
        return componentRef;
    }
    detachComponent(componentRef) {
        this.portalHostService.appRef.attachView(componentRef.hostView);
        this.setDisposeFn(() => {
            this.portalHostService.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
        });
    }
    addNewToast(componentRef, newestOnTop) {
        if (newestOnTop) {
            this._hostDomElement.insertBefore(this._getComponentRootNode(componentRef), this._hostDomElement.firstChild);
        }
        else {
            this._hostDomElement.appendChild(this._getComponentRootNode(componentRef));
        }
    }
    _getComponentRootNode(componentRef) {
        return componentRef.hostView.rootNodes[0];
    }
}

class OverlayRef {
    _portalHost;
    constructor(_portalHost) {
        this._portalHost = _portalHost;
    }
    attach(portal, newestOnTop = true) {
        return this._portalHost.attach(portal, newestOnTop);
    }
    detach() {
        return this._portalHost.detach();
    }
}

class OverlayContainer {
    document;
    containerElement;
    constructor(document) {
        this.document = document;
    }
    ngOnDestroy() {
        if (this.containerElement?.parentNode) {
            this.containerElement.parentNode.removeChild(this.containerElement);
        }
    }
    getContainerElement() {
        if (!this.containerElement) {
            this.createContainer();
        }
        return this.containerElement;
    }
    createContainer() {
        const container = this.document.createElement('div');
        container.classList.add('overlay-container');
        this.document.body.appendChild(container);
        this.containerElement = container;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayContainer, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayContainer, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayContainer, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class OverlayService {
    _overlayContainer;
    _document;
    overlayContainer;
    document;
    constructor(_overlayContainer, _document) {
        this._overlayContainer = _overlayContainer;
        this._document = _document;
        this.overlayContainer = _overlayContainer;
        this.document = _document;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayService, deps: [{ token: OverlayContainer }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: OverlayService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: OverlayContainer }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class PortalHostService {
    _componentFactoryResolver;
    _appRef;
    componentFactoryResolver;
    appRef;
    constructor(_componentFactoryResolver, _appRef) {
        this._componentFactoryResolver = _componentFactoryResolver;
        this._appRef = _appRef;
        this.componentFactoryResolver = _componentFactoryResolver;
        this.appRef = _appRef;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PortalHostService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PortalHostService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: PortalHostService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }] });

class Overlay {
    overlayService;
    portalHostService;
    constructor(overlayService, portalHostService) {
        this.overlayService = overlayService;
        this.portalHostService = portalHostService;
    }
    create(positionClass, toastrContainer) {
        return this._createOverlayRef(this.getPaneElement(positionClass, toastrContainer));
    }
    getPaneElement(positionClass = '', toastrContainer) {
        const overlayContainer = this.overlayService.overlayContainer.getContainerElement();
        const overlayElementsWWithPositionClass = overlayContainer.getElementsByClassName(positionClass);
        const existsPaneElement = overlayElementsWWithPositionClass.length > 0;
        if (existsPaneElement) {
            return Array.from(overlayElementsWWithPositionClass)[0];
        }
        return this._createPaneElement(positionClass, toastrContainer);
    }
    _createPaneElement(positionClass, toastrContainer) {
        const pane = this.overlayService.document.createElement('div');
        pane.classList.add(positionClass);
        pane.classList.add('centric-toast-container');
        if (toastrContainer) {
            toastrContainer.getContainerElement().appendChild(pane);
        }
        else {
            this.overlayService.overlayContainer.getContainerElement().appendChild(pane);
        }
        return pane;
    }
    _createOverlayRef(pane) {
        return new OverlayRef(this._createPortalHost(pane));
    }
    _createPortalHost(pane) {
        return new DomPortalHost(pane, this.portalHostService);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Overlay, deps: [{ token: OverlayService }, { token: PortalHostService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Overlay, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Overlay, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: OverlayService }, { type: PortalHostService }] });

const DEFAULT_NO_COMPONENT_GLOBAL_CONFIG = {
    maxOpened: 0,
    autoDismiss: false,
    newestOnTop: true,
    preventDuplicates: false,
    resetTimeoutOnDuplicate: false,
    closeButton: true,
    disableTimeOut: false,
    timeOut: 5000,
    extendedTimeOut: 1000,
    enableHtml: false,
    progressBar: false,
    toastClass: 'centric-toast',
    positionClass: 'toast-bottom-right',
    titleClass: 'toast-title',
    messageClass: 'toast-message',
    easing: 'ease-in',
    easeTime: 300,
    tapToDismiss: true,
    onActivateTick: false,
    progressAnimation: 'decreasing'
};
const TOAST_CONFIG = new InjectionToken('ToastConfig');
const provideToastr = (config = {}) => {
    const providers = [
        {
            provide: TOAST_CONFIG,
            useValue: {
                default: DEFAULT_NO_COMPONENT_GLOBAL_CONFIG,
                config
            }
        }
    ];
    return makeEnvironmentProviders(providers);
};
class ToastPackage {
    toastConfigService;
    toastPropService;
    _onTap = new Subject();
    _onAction = new Subject();
    get config() {
        return this.toastConfigService.config;
    }
    get toastRef() {
        return this.toastConfigService.toastRef;
    }
    get toastId() {
        return this.toastConfigService.toastId;
    }
    get dataTestId() {
        return this.toastConfigService.dataTestId;
    }
    get message() {
        return this.toastPropService.message;
    }
    get title() {
        return this.toastPropService.title;
    }
    get toastType() {
        return this.toastPropService.toastType;
    }
    get iconName() {
        return this.toastPropService.iconName;
    }
    constructor(toastConfigService, toastPropService) {
        this.toastConfigService = toastConfigService;
        this.toastPropService = toastPropService;
        toastConfigService.toastRef?.afterClosed().subscribe(() => {
            this._onAction.complete();
            this._onTap.complete();
        });
    }
    triggerTap() {
        this._onTap.next();
        if (this.config.tapToDismiss) {
            this._onTap.complete();
        }
    }
    onTap() {
        return this._onTap.asObservable();
    }
    triggerAction(action) {
        this._onAction.next(action);
    }
    onAction() {
        return this._onAction.asObservable();
    }
}

class ToastInjector {
    _toastPackage;
    _parentInjector;
    constructor(_toastPackage, _parentInjector) {
        this._toastPackage = _toastPackage;
        this._parentInjector = _parentInjector;
    }
    get(token, notFoundValue, options) {
        if (token === ToastPackage) {
            return this._toastPackage;
        }
        return this._parentInjector.get(token, notFoundValue, options);
    }
}

class ToastRef {
    _overlayRef;
    componentInstance;
    _afterClosed = new Subject();
    _activate = new Subject();
    _manualClose = new Subject();
    _resetTimeout = new Subject();
    constructor(_overlayRef) {
        this._overlayRef = _overlayRef;
    }
    manualClose() {
        this._manualClose.next();
        this._manualClose.complete();
    }
    manualClosed() {
        return this._manualClose.asObservable();
    }
    timeoutReset() {
        return this._resetTimeout.asObservable();
    }
    /**
     * Close the toast.
     */
    close() {
        this._overlayRef.detach();
        this._afterClosed.next();
        this._manualClose.next();
        this._afterClosed.complete();
        this._manualClose.complete();
        this._activate.complete();
        this._resetTimeout.complete();
    }
    afterClosed() {
        return this._afterClosed.asObservable();
    }
    isInactive() {
        return this._activate.isStopped;
    }
    activate() {
        this._activate.next();
        this._activate.complete();
    }
    afterActivate() {
        return this._activate.asObservable();
    }
    resetTimeout() {
        this._resetTimeout.next();
    }
}

class ToastContainerDirective {
    el;
    constructor(el) {
        this.el = el;
    }
    getContainerElement() {
        return this.el.nativeElement;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastContainerDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: ToastContainerDirective, isStandalone: true, selector: "[toastContainer]", exportAs: ["toastContainer"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[toastContainer]',
                    exportAs: 'toastContainer',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

const WIDTH = -1;
const INTERVAL = 10;
const PROGRESS_WIDTH = 100;
const DEFAULT_TIMEOUT = 5000;
const DEFAULT_EXTENDED_TIMEOUT = 1000;
const FULL_VERSION_ICON_SIZE = '48';
const COMPACT_VERSION_ICON_SIZE = '20';
/**
 * To use this component in a module, you can import the `CentricToastrModule`:
 * @example
 * ```typescript
 * import { CentricToastrModule } from '@windmill/ng-windmill/toastr';
 * ```
 *
 * To use the standalone component directly, you can import `ToastrComponent`:
 * @example
 * ```typescript
 * import { ToastrComponent } from '@windmill/ng-windmill/toastr';
 * ```
 *
 * If you are using the standalone component you can pass values to provideToastr() to set a global configuration:
 * @example
 * ```typescript
 *     providers: [ provideToastr({
 *                      positionClass: 'toast-top-right',
 *                      ...
 *                  })
 *                ]
 * ```
 */
class ToastrComponent {
    toastPackage;
    breakpointObserver;
    responsiveService;
    ngZone;
    /**
     * Message of the toast
     */
    message;
    /**
     * Title of the toast
     */
    title;
    /**
     * The configuration
     */
    options;
    /**
     * Timeout
     */
    originalTimeout;
    /**
     * The name of the icon displayed in toast
     */
    iconName;
    /**
     * The background type of the toast; if you want a light background should be set with 'toast-light'
     */
    toastBackground;
    /**
     * Data-testId
     */
    dataTestId;
    /**
     * Width of progress bar
     */
    width = WIDTH;
    isSmallOrMediumBreakpoint = false;
    /**
     * Event emitted when the toast is closed
     */
    // eslint-disable-next-line @angular-eslint/no-output-native
    close = new EventEmitter();
    /**
     * A combination of toast type and options.toastClass
     */
    toastClasses = '';
    /** controls animation */
    state = {
        value: 'inactive',
        params: {
            easeTime: this.easeTime,
            easing: 'ease-in'
        }
    };
    get titleClass() {
        return this.options?.titleClass;
    }
    get easeTime() {
        return this._easeTime;
    }
    set easeTime(value) {
        this._easeTime = value;
        this.state = {
            value: 'inactive',
            params: {
                easeTime: this._easeTime,
                easing: 'ease-in'
            }
        };
    }
    get toastIconSize() {
        return this.isSmallOrMediumBreakpoint ? COMPACT_VERSION_ICON_SIZE : FULL_VERSION_ICON_SIZE;
    }
    get closeButtonType() {
        if (!this.isSmallOrMediumBreakpoint && this.toastBackground === 'toast-light') {
            return 'icon-ghost-greyscale';
        }
        else if (this.toastPackage.toastType === 'toast-info') {
            return 'icon-high-emphasis-theme';
        }
        else if (this.toastPackage.toastType === 'toast-success') {
            return 'icon-high-emphasis-success';
        }
        else if (this.toastPackage.toastType === 'toast-error') {
            return 'icon-high-emphasis-danger';
        }
        else if (this.toastPackage.toastType === 'toast-warning') {
            return 'icon-high-emphasis-warning';
        }
        return '';
    }
    timeout;
    intervalId;
    hideTime;
    _easeTime;
    breakpointObserverSubscription;
    activateSubscription;
    removeSubscription;
    resetSubscription;
    constructor(toastPackage, breakpointObserver, responsiveService, ngZone) {
        this.toastPackage = toastPackage;
        this.breakpointObserver = breakpointObserver;
        this.responsiveService = responsiveService;
        this.ngZone = ngZone;
        this.attributeConfigurations(toastPackage);
        this.activateSubscription = toastPackage.toastRef?.afterActivate().subscribe(() => {
            this.activateToast();
        });
        this.removeSubscription = toastPackage.toastRef?.manualClosed().subscribe(() => {
            this.remove();
        });
        this.resetSubscription = toastPackage.toastRef?.timeoutReset().subscribe(() => {
            this.resetTimeout();
        });
    }
    ngOnInit() {
        this.breakpointObserverSubscription = this.breakpointObserver
            .observe([this.responsiveService.breakpoints.small, this.responsiveService.breakpoints.medium])
            .subscribe((result) => {
            this.isSmallOrMediumBreakpoint = result.matches;
        });
    }
    ngOnDestroy() {
        this.activateSubscription?.unsubscribe();
        this.removeSubscription?.unsubscribe();
        this.resetSubscription?.unsubscribe();
        this.breakpointObserverSubscription?.unsubscribe();
        clearInterval(this.intervalId);
        clearTimeout(this.timeout);
    }
    onKeydownHandler(event) {
        if (event.key === 'Escape') {
            this.remove();
        }
    }
    tapToast() {
        if (this.state.value === 'removed') {
            return;
        }
        this.toastPackage.triggerTap();
        if (this.options?.tapToDismiss) {
            this.remove();
        }
    }
    stickAround() {
        if (this.state.value === 'removed') {
            return;
        }
        clearTimeout(this.timeout);
        if (this.options) {
            this.options.timeOut = 0;
        }
        this.hideTime = 0;
        // disable progressBar
        clearInterval(this.intervalId);
        this.width = 0;
    }
    delayedHideToast() {
        if (this.options?.disableTimeOut || this.options?.extendedTimeOut === 0 || this.state.value === 'removed') {
            return;
        }
        this.extendTimeout();
        this.hideTime = new Date().getTime() + (this.options?.timeOut || 0);
        this.width = WIDTH;
        this.setProgressInterval();
    }
    /**
     * activates toast and sets timeout
     */
    activateToast() {
        this.state = { ...this.state, value: 'active' };
        if (!this.options?.disableTimeOut && this.options?.timeOut) {
            this.outsideTimeout(() => this.remove(), this.options.timeOut);
            this.hideTime = new Date().getTime() + this.options.timeOut;
            this.setProgressInterval();
        }
    }
    /**
     * updates progress bar width
     */
    updateProgress() {
        if ([0, PROGRESS_WIDTH].includes(this.width) || !this.options?.timeOut) {
            return;
        }
        this.createProgressWidth();
        if (this.options.progressAnimation === 'increasing') {
            this.width = PROGRESS_WIDTH - this.width;
        }
        this.checkProgressLimits();
    }
    /**
     * Reset timeout
     */
    resetTimeout() {
        clearTimeout(this.timeout);
        clearInterval(this.intervalId);
        this.state = { ...this.state, value: 'active' };
        const actualTimeout = this.originalTimeout ?? DEFAULT_TIMEOUT;
        this.outsideTimeout(() => this.remove(), actualTimeout);
        if (this.options) {
            this.options.timeOut = this.originalTimeout;
        }
        this.hideTime = new Date().getTime() + (this.options?.timeOut || 0);
        this.width = WIDTH;
        this.setProgressInterval();
    }
    /**
     * Tells toastrService to remove this toast after animation time
     */
    remove() {
        if (this.state.value === 'removed') {
            return;
        }
        clearTimeout(this.timeout);
        this.state = { ...this.state, value: 'removed' };
        this.outsideTimeout(() => this.close.emit(this.toastPackage?.toastId), Number(this.toastPackage.config.easeTime));
    }
    extendTimeout() {
        if (this.options) {
            const actualExtendedTimeout = this.options.extendedTimeOut ?? DEFAULT_EXTENDED_TIMEOUT;
            this.outsideTimeout(() => this.remove(), actualExtendedTimeout);
            this.options.timeOut = this.options?.extendedTimeOut;
        }
    }
    attributeConfigurations(toastPackage) {
        this.easeTime = toastPackage.config.easeTime;
        this.message = toastPackage.message;
        this.title = toastPackage.title;
        this.iconName = toastPackage.iconName;
        this.dataTestId = toastPackage.dataTestId;
        this.toastBackground = toastPackage.config.toastBackground;
        this.options = toastPackage.config;
        this.originalTimeout = toastPackage.config.timeOut;
        this.toastClasses = `${toastPackage.toastType} ${toastPackage.config.toastClass}`;
    }
    outsideTimeout(func, timeout) {
        if (this.ngZone) {
            this.ngZone.runOutsideAngular(() => {
                this.timeout = setTimeout(() => this.runInsideAngular(func), timeout);
            });
        }
        else {
            this.timeout = setTimeout(() => func(), timeout);
        }
    }
    outsideInterval(func, timeout) {
        if (this.ngZone) {
            this.ngZone.runOutsideAngular(() => {
                this.intervalId = setInterval(() => this.runInsideAngular(func), timeout);
            });
        }
        else {
            this.intervalId = setInterval(() => func(), timeout);
        }
    }
    runInsideAngular(func) {
        if (this.ngZone) {
            this.ngZone.run(() => func());
        }
        else {
            func();
        }
    }
    setProgressInterval() {
        if (this.options?.progressBar) {
            this.outsideInterval(() => this.updateProgress(), INTERVAL);
        }
    }
    createProgressWidth() {
        const NOW = new Date().getTime();
        const actualHideTime = this.hideTime ?? 0;
        const actualTimeout = this.options?.timeOut ?? DEFAULT_TIMEOUT;
        const REMAINING = actualHideTime - NOW;
        this.width = (REMAINING / actualTimeout) * PROGRESS_WIDTH;
    }
    checkProgressLimits() {
        if (this.width < 0) {
            this.width = 0;
        }
        else if (this.width > PROGRESS_WIDTH) {
            this.width = PROGRESS_WIDTH;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastrComponent, deps: [{ token: ToastPackage }, { token: i2.BreakpointObserver }, { token: i3.ResponsiveService }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: ToastrComponent, isStandalone: true, selector: "centric-toastr", outputs: { close: "close" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)", "click": "tapToast()", "mouseenter": "stickAround()", "mouseleave": "delayedHideToast()" }, properties: { "class": "this.toastClasses", "@flyInOut": "this.state" } }, ngImport: i0, template: "<div\r\n  class=\"toastr-background\"\r\n  [ngClass]=\"toastBackground\"\r\n  [class.has-title]=\"title\"\r\n  [class.has-message]=\"message\"\r\n  [class.has-compact-version]=\"isSmallOrMediumBreakpoint\"\r\n  [attr.data-testid]=\"dataTestId\">\r\n  <div class=\"toastr-container\">\r\n    @if (isSmallOrMediumBreakpoint) {\r\n      <div class=\"toast-header\">\r\n        <centric-icon2\r\n          class=\"toast-icon\"\r\n          [svgIcon]=\"iconName\"\r\n          [size]=\"toastIconSize\">\r\n        </centric-icon2>\r\n        @if (title) {\r\n          <div\r\n            [ngClass]=\"titleClass\"\r\n            class=\"windmill-toast-title\"\r\n            role=\"alert\"\r\n            aria-live=\"polite\">\r\n            {{ title }}\r\n          </div>\r\n        }\r\n        @if (options?.closeButton) {\r\n          <button\r\n            class=\"centric-inner-button small toast-close-button\"\r\n            [ngClass]=\"closeButtonType\"\r\n            aria-hidden=\"true\"\r\n            tabindex=\"-1\"\r\n            (click)=\"remove()\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_b'\"\r\n              [size]=\"'20'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n      @if (message) {\r\n        <div class=\"toast-content\">\r\n          <ng-template [ngTemplateOutlet]=\"messageTemplate\"></ng-template>\r\n        </div>\r\n      }\r\n    } @else {\r\n      <div class=\"toast-side-panel\">\r\n        <centric-icon2\r\n          class=\"toast-icon\"\r\n          [svgIcon]=\"iconName\"\r\n          [size]=\"toastIconSize\">\r\n        </centric-icon2>\r\n      </div>\r\n      <div\r\n        class=\"toast-content\"\r\n        [class.has-close-button]=\"options?.closeButton\">\r\n        @if (title) {\r\n          <div\r\n            [ngClass]=\"titleClass\"\r\n            class=\"windmill-toast-title\"\r\n            role=\"alert\"\r\n            aria-live=\"polite\">\r\n            {{ title }}\r\n          </div>\r\n        }\r\n        @if (message) {\r\n          <ng-template [ngTemplateOutlet]=\"messageTemplate\"></ng-template>\r\n        }\r\n        @if (options?.closeButton) {\r\n          <button\r\n            class=\"centric-inner-button small toast-close-button\"\r\n            [ngClass]=\"closeButtonType\"\r\n            aria-hidden=\"true\"\r\n            tabindex=\"-1\"\r\n            (click)=\"remove()\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_b'\"\r\n              [size]=\"'20'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n    }\r\n\r\n    @if (options?.progressBar) {\r\n      <div\r\n        class=\"toast-progress\"\r\n        [style.width]=\"width + '%'\"></div>\r\n    }\r\n  </div>\r\n</div>\r\n\r\n<ng-template #messageTemplate>\r\n  @if (options?.enableHtml) {\r\n    <div\r\n      [class]=\"options?.messageClass\"\r\n      [innerHTML]=\"message\"\r\n      role=\"alert\"\r\n      aria-live=\"polite\"></div>\r\n  } @else {\r\n    <div\r\n      [class]=\"options?.messageClass\"\r\n      role=\"alert\"\r\n      aria-live=\"polite\">\r\n      {{ message }}\r\n    </div>\r\n  }\r\n</ng-template>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], animations: [
            trigger('flyInOut', [
                state('inactive', style({
                    opacity: 0
                })),
                state('active', style({})),
                state('removed', style({ opacity: 0 })),
                transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
                transition('active => removed', animate('{{ easeTime }}ms {{ easing }}'))
            ])
        ], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastrComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-toastr', encapsulation: ViewEncapsulation.None, animations: [
                        trigger('flyInOut', [
                            state('inactive', style({
                                opacity: 0
                            })),
                            state('active', style({})),
                            state('removed', style({ opacity: 0 })),
                            transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
                            transition('active => removed', animate('{{ easeTime }}ms {{ easing }}'))
                        ])
                    ], preserveWhitespaces: false, imports: [CommonModule, NgClass, NgTemplateOutlet, CentricIcon2Component], standalone: true, template: "<div\r\n  class=\"toastr-background\"\r\n  [ngClass]=\"toastBackground\"\r\n  [class.has-title]=\"title\"\r\n  [class.has-message]=\"message\"\r\n  [class.has-compact-version]=\"isSmallOrMediumBreakpoint\"\r\n  [attr.data-testid]=\"dataTestId\">\r\n  <div class=\"toastr-container\">\r\n    @if (isSmallOrMediumBreakpoint) {\r\n      <div class=\"toast-header\">\r\n        <centric-icon2\r\n          class=\"toast-icon\"\r\n          [svgIcon]=\"iconName\"\r\n          [size]=\"toastIconSize\">\r\n        </centric-icon2>\r\n        @if (title) {\r\n          <div\r\n            [ngClass]=\"titleClass\"\r\n            class=\"windmill-toast-title\"\r\n            role=\"alert\"\r\n            aria-live=\"polite\">\r\n            {{ title }}\r\n          </div>\r\n        }\r\n        @if (options?.closeButton) {\r\n          <button\r\n            class=\"centric-inner-button small toast-close-button\"\r\n            [ngClass]=\"closeButtonType\"\r\n            aria-hidden=\"true\"\r\n            tabindex=\"-1\"\r\n            (click)=\"remove()\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_b'\"\r\n              [size]=\"'20'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n      @if (message) {\r\n        <div class=\"toast-content\">\r\n          <ng-template [ngTemplateOutlet]=\"messageTemplate\"></ng-template>\r\n        </div>\r\n      }\r\n    } @else {\r\n      <div class=\"toast-side-panel\">\r\n        <centric-icon2\r\n          class=\"toast-icon\"\r\n          [svgIcon]=\"iconName\"\r\n          [size]=\"toastIconSize\">\r\n        </centric-icon2>\r\n      </div>\r\n      <div\r\n        class=\"toast-content\"\r\n        [class.has-close-button]=\"options?.closeButton\">\r\n        @if (title) {\r\n          <div\r\n            [ngClass]=\"titleClass\"\r\n            class=\"windmill-toast-title\"\r\n            role=\"alert\"\r\n            aria-live=\"polite\">\r\n            {{ title }}\r\n          </div>\r\n        }\r\n        @if (message) {\r\n          <ng-template [ngTemplateOutlet]=\"messageTemplate\"></ng-template>\r\n        }\r\n        @if (options?.closeButton) {\r\n          <button\r\n            class=\"centric-inner-button small toast-close-button\"\r\n            [ngClass]=\"closeButtonType\"\r\n            aria-hidden=\"true\"\r\n            tabindex=\"-1\"\r\n            (click)=\"remove()\">\r\n            <centric-icon2\r\n              [svgIcon]=\"'cancel_b'\"\r\n              [size]=\"'20'\">\r\n            </centric-icon2>\r\n          </button>\r\n        }\r\n      </div>\r\n    }\r\n\r\n    @if (options?.progressBar) {\r\n      <div\r\n        class=\"toast-progress\"\r\n        [style.width]=\"width + '%'\"></div>\r\n    }\r\n  </div>\r\n</div>\r\n\r\n<ng-template #messageTemplate>\r\n  @if (options?.enableHtml) {\r\n    <div\r\n      [class]=\"options?.messageClass\"\r\n      [innerHTML]=\"message\"\r\n      role=\"alert\"\r\n      aria-live=\"polite\"></div>\r\n  } @else {\r\n    <div\r\n      [class]=\"options?.messageClass\"\r\n      role=\"alert\"\r\n      aria-live=\"polite\">\r\n      {{ message }}\r\n    </div>\r\n  }\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: ToastPackage }, { type: i2.BreakpointObserver }, { type: i3.ResponsiveService }, { type: i0.NgZone }], propDecorators: { close: [{
                type: Output
            }], toastClasses: [{
                type: HostBinding,
                args: ['class']
            }], state: [{
                type: HostBinding,
                args: ['@flyInOut']
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }], tapToast: [{
                type: HostListener,
                args: ['click']
            }], stickAround: [{
                type: HostListener,
                args: ['mouseenter']
            }], delayedHideToast: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class ToastBaseService {
    _overlay;
    _injector;
    _sanitizer;
    _ngZone;
    overlay;
    injector;
    sanitizer;
    ngZone;
    constructor(_overlay, _injector, _sanitizer, _ngZone) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._sanitizer = _sanitizer;
        this._ngZone = _ngZone;
        this.overlay = _overlay;
        this.injector = _injector;
        this.sanitizer = _sanitizer;
        this.ngZone = _ngZone;
    }
    createToastConfigArguments(toastConfigurationService, index) {
        return {
            config: toastConfigurationService.config,
            toastRef: toastConfigurationService.toastRef,
            toastId: index,
            dataTestId: toastConfigurationService.dataTestId
        };
    }
    createToastPropertiesArgument(toastPropService, sanitizedMessage) {
        return {
            iconName: toastPropService.iconName,
            message: sanitizedMessage,
            title: toastPropService.title,
            toastType: toastPropService.toastType
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastBaseService, deps: [{ token: Overlay }, { token: i0.Injector }, { token: i2$1.DomSanitizer }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastBaseService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastBaseService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Overlay }, { type: i0.Injector }, { type: i2$1.DomSanitizer }, { type: i0.NgZone }] });

class ToastrService {
    toastBaseService;
    toastrConfig;
    currentlyActive = 0;
    toasts = [];
    overlayContainer;
    index = 0;
    constructor(token, toastBaseService) {
        this.toastBaseService = toastBaseService;
        this.toastrConfig = {
            ...token.default,
            ...token.config
        };
    }
    /** Show successful toast */
    success(message, title, override = {}, dataTestId) {
        return this._preBuildNotification({ message, title, toastType: 'toast-success', iconName: 'check-circle_r' }, { config: this.applyConfig(override), dataTestId, toastId: null, toastRef: null });
    }
    /** Show error toast */
    error(message, title, override = {}, dataTestId) {
        return this._preBuildNotification({ message, title, toastType: 'toast-error', iconName: 'exclamation-triangle_r' }, { config: this.applyConfig(override), dataTestId, toastId: null, toastRef: null });
    }
    /** Show info toast */
    info(message, title, override = {}, dataTestId) {
        return this._preBuildNotification({ message, title, toastType: 'toast-info', iconName: 'info-circle_r' }, { config: this.applyConfig(override), dataTestId, toastId: null, toastRef: null });
    }
    /** Show warning toast */
    warning(message, title, override = {}, dataTestId) {
        return this._preBuildNotification({ message, title, toastType: 'toast-warning', iconName: 'exclamation-circle_r' }, { config: this.applyConfig(override), dataTestId, toastId: null, toastRef: null });
    }
    /**
     * Remove all or a single toast by id
     */
    clear(toastId) {
        // Call every toastRef manualClose function
        for (const toast of this.toasts) {
            if (typeof toastId === 'undefined') {
                toast.toastRef.manualClose();
            }
            else if (toast.toastId === toastId) {
                toast.toastRef.manualClose();
                return;
            }
        }
    }
    /**
     * Remove and destroy a single toast by id
     */
    remove(toastId) {
        const found = this._findToast(toastId);
        if (!found) {
            return false;
        }
        found.activeToast.toastRef.close();
        this.toasts.splice(found.index, 1);
        this.currentlyActive = this.currentlyActive - 1;
        if (!this.toastrConfig.maxOpened || !this.toasts.length) {
            return false;
        }
        this.activateNextToast();
        return true;
    }
    activateNextToast() {
        if (this.currentlyActive < this.toastrConfig.maxOpened && this.toasts[this.currentlyActive]) {
            const activeToastRef = this.toasts[this.currentlyActive].toastRef;
            if (!activeToastRef.isInactive()) {
                this.currentlyActive = this.currentlyActive + 1;
                activeToastRef.activate();
            }
        }
    }
    /**
     * Finds a duplicate toast if one exists
     */
    findDuplicate(message, resetOnDuplicate) {
        for (const toast of this.toasts) {
            if (toast.message === message) {
                if (resetOnDuplicate) {
                    toast.toastRef.resetTimeout();
                }
                return toast;
            }
        }
        return null;
    }
    /** Create a clone of global config and apply individual settings */
    applyConfig(override = {}) {
        return { ...this.toastrConfig, ...override };
    }
    /**
     * Find toast object by id
     */
    _findToast(toastId) {
        for (let i = 0; i < this.toasts.length; i++) {
            if (this.toasts[i].toastId === toastId) {
                return { index: i, activeToast: this.toasts[i] };
            }
        }
        return null;
    }
    /**
     * Determines the need to run inside angular's zone then builds the toast
     */
    _preBuildNotification(toastPropService, toastConfigurationService) {
        const toastPropArguments = {
            message: toastPropService.message,
            title: toastPropService.title,
            toastType: toastPropService.toastType,
            iconName: toastPropService.iconName
        };
        if (toastConfigurationService.config.onActivateTick) {
            return this.toastBaseService.ngZone.run(() => this._buildNotification(toastPropArguments, toastConfigurationService));
        }
        return this._buildNotification(toastPropArguments, toastConfigurationService);
    }
    /**
     * Creates and attaches toast data to component
     * Returns the active toast, or in case preventDuplicates is enabled the original/non-duplicate active toast.
     */
    _buildNotification(toastPropService, toastConfigurationService) {
        const hasDuplicate = this.checkDuplicate(toastPropService);
        if (hasDuplicate) {
            return hasDuplicate;
        }
        const isActive = this.clearToast();
        const activeToast = this.createToastOverlay(toastPropService, toastConfigurationService);
        this.activateToast(isActive, activeToast);
        this.toasts.push(activeToast);
        return activeToast;
    }
    checkDuplicate(toastPropService) {
        if (toastPropService.message && this.toastrConfig.preventDuplicates) {
            const duplicate = this.findDuplicate(toastPropService.message, this.toastrConfig.resetTimeoutOnDuplicate);
            if (duplicate !== null) {
                return duplicate;
            }
        }
        return null;
    }
    clearToast() {
        let keepInactive = false;
        if (this.toastrConfig.maxOpened && this.currentlyActive >= this.toastrConfig.maxOpened) {
            keepInactive = true;
            if (this.toastrConfig.autoDismiss) {
                this.clear(this.toasts[0].toastId);
            }
        }
        return keepInactive;
    }
    activateToast(isActive, activeToast) {
        if (!isActive) {
            setTimeout(() => {
                activeToast.toastRef.activate();
                this.currentlyActive = this.currentlyActive + 1;
            });
        }
    }
    createToastOverlay(toastPropService, toastConfigurationService) {
        const overlayRef = this.createOverlayRef(toastConfigurationService);
        this.index = this.index + 1;
        const toastRef = new ToastRef(overlayRef);
        const toastPackage = this.createToastPackage(toastPropService, {
            config: toastConfigurationService.config,
            dataTestId: toastConfigurationService.dataTestId,
            toastRef,
            toastId: null
        });
        const toastInjector = new ToastInjector(toastPackage, this.toastBaseService.injector);
        const toastComponent = toastConfigurationService.config.toastComponent ?? ToastrComponent;
        const component = new ComponentPortal(toastComponent, toastInjector);
        const portal = overlayRef.attach(component, this.toastrConfig.newestOnTop);
        return this.createActiveToastData(toastRef, portal, toastPropService, toastPackage);
    }
    createOverlayRef(toastConfigurationService) {
        return this.toastBaseService.overlay.create(toastConfigurationService.config.positionClass, this.overlayContainer);
    }
    createToastPackage(toastPropService, toastConfigurationService) {
        let sanitizedMessage = toastPropService.message;
        if (toastPropService.message && toastConfigurationService.config.enableHtml) {
            sanitizedMessage = this.toastBaseService.sanitizer.sanitize(SecurityContext.HTML, toastPropService.message);
        }
        const toastConfigArguments = this.toastBaseService.createToastConfigArguments(toastConfigurationService, this.index);
        const toastPropArguments = this.toastBaseService.createToastPropertiesArgument(toastPropService, sanitizedMessage);
        return new ToastPackage(toastConfigArguments, toastPropArguments);
    }
    createActiveToastData(toastRef, portal, toastPropService, toastPackage) {
        this.closeSubscribe(toastRef, portal);
        const activeToast = {
            toastId: this.index,
            message: toastPropService.message || '',
            toastRef,
            onShown: toastRef.afterActivate(),
            onHidden: toastRef.afterClosed(),
            onTap: toastPackage.onTap(),
            onAction: toastPackage.onAction(),
            portal
        };
        return activeToast;
    }
    closeSubscribe(toastRef, portal) {
        toastRef.componentInstance = portal.instance;
        if (portal.instance?.close && portal.instance.close instanceof EventEmitter) {
            portal.instance.close.subscribe(toastId => this.remove(toastId));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastrService, deps: [{ token: TOAST_CONFIG }, { token: ToastBaseService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastrService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToastrService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TOAST_CONFIG]
                }] }, { type: ToastBaseService }] });

const moduleComponents = [ToastrComponent];
const defaultGlobalConfig = {
    ...DEFAULT_NO_COMPONENT_GLOBAL_CONFIG,
    toastComponent: ToastrComponent
};
class CentricToastrModule {
    static forRoot(config = {}) {
        return {
            ngModule: CentricToastrModule,
            providers: [
                {
                    provide: TOAST_CONFIG,
                    useValue: {
                        default: defaultGlobalConfig,
                        config
                    }
                }
            ]
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricToastrModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricToastrModule, imports: [CommonModule, CentricIcon2Module, ToastrComponent], exports: [ToastrComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricToastrModule, providers: [ToastrService], imports: [CommonModule, CentricIcon2Module, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricToastrModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents],
                    providers: [ToastrService],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BasePortalHost, CentricToastrModule, ComponentPortal, DEFAULT_NO_COMPONENT_GLOBAL_CONFIG, Overlay, OverlayContainer, OverlayRef, TOAST_CONFIG, ToastContainerDirective, ToastInjector, ToastPackage, ToastRef, ToastrComponent, ToastrService, defaultGlobalConfig, provideToastr };
